import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, injectIntl } from 'react-intl';

import env from 'Env';
import {
  fetchProduct,
} from 'Redux/ducks/product';
import NotFound from 'Components/NotFound/NotFound';
import Loading from 'Components/Utility/Loading';
import Meta from 'Components/Utility/SEO/Meta';
import ProductBreadcrumbs from 'Components/Utility/SEO/ProductBreadcrumbs';
import ProductDLC from 'Components/ProductDLC/ProductDLC';
import { fetchBySlugs } from 'Redux/ducks/algolia-calls';

const PRODUCT_DLC_PAGE_THRESHOLD = env.productDlcPageThreshold;

const ProductDLCContainer = ({
  match,
  intl: { formatMessage },
}) => {
  const [product, setProduct] = useState(null);
  const [editionHits, setEditionHits] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [redirect, setRedirect] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  const messages = defineMessages({
    title: {
      id: 'productdlccontainer.title',
      defaultMessage: '{productName} DLC',
    },
    editionTitle: {
      id: 'productdlccontainer.editionTitle',
      defaultMessage: 'All {productName} Editions',
    },
    seotitle: {
      id: 'productdlccontainer.seotitle',
      defaultMessage: '{productName} DLC',
    },
    description: {
      id: 'productdlccontainer.description',
      defaultMessage: 'Browse all the DLC available for {productName} to get the most from your favourite PC Game',
    },
  });


  const slug = match.params.slug;

  useEffect(() => {
    const loadProduct = () => {
      setIsLoading(true);

      return fetchProduct(slug)
        .then((result) => {
          if (result.notFound) {
            setTimeout(() => {
              window.prerenderReady = true;
            }, 1500);
            return setNotFound(true);
          }

          if (result.redirect) {
            return setRedirect(result.redirect);
          }

          const foundProduct = result.product;

          setNotFound(false);
          setProduct(foundProduct);

          if (foundProduct && foundProduct.editions && foundProduct.editions.length > 1) {
            fetchBySlugs([foundProduct.editions.map(e => e.slug)], 8)
              .then((results) => {
                setTimeout(() => {
                  window.prerenderReady = true;
                }, 1500);
                setEditionHits(results[0]);
              })
              .catch(() => {});
          } else {
            setTimeout(() => {
              window.prerenderReady = true;
            }, 1500);
          }

          return setIsLoading(false);
        }).catch(setFetchError);
    };

    if (slug) {
      loadProduct();
    } else {
      setNotFound(true);
    }
  }, [slug]);

  if (fetchError) {
    setTimeout(() => {
      window.prerenderReady = true;
    }, 1500);
    throw fetchError;
  }

  if (redirect) {
    setTimeout(() => {
      window.prerenderReady = true;
    }, 1500);
    return <NotFound redirect={redirect} />;
  }

  if (notFound) return <NotFound />;

  if (isLoading) {
    return <Loading />;
  }

  if (!product) return false;

  const dlc = product.relatedHits && product.relatedHits.filter(h => h.type === 'dlc');

  const dlcCountThreshold = product.type === 'dlc' ? PRODUCT_DLC_PAGE_THRESHOLD - 1 : PRODUCT_DLC_PAGE_THRESHOLD;

  if ((dlc || []).length < dlcCountThreshold) {
    return <NotFound />;
  }

  const editions = product.editions;

  if (editions.length) {
    const baseEdition = editions.find(e => !e.parent_slug);
    if (baseEdition && baseEdition.slug !== product.slug) {
      return <NotFound redirect={`${baseEdition.type}/${baseEdition.slug}/dlc`} />;
    }
  }

  if (product.base_game) {
    return <NotFound redirect={`${product.base_game.type}/${product.base_game.slug}/dlc`} />;
  }

  const title = formatMessage(messages.title, { productName: product.name });
  const editionTitle = formatMessage(messages.editionTitle, { productName: product.name });
  const seotitle = formatMessage(messages.seotitle, { productName: product.name });
  const description =
    product.dlc_desc || formatMessage(messages.description, { productName: product.name });

  return (
    <React.Fragment>
      <Meta
        brandTitle
        title={seotitle}
        description={description}
        url={`${product.type}/${product.slug}/dlc`}
        pageType="ItemPage"
      />
      <ProductBreadcrumbs isDLC product={product} />
      <ProductDLC
        product={product}
        dlc={dlc}
        editions={editionHits}
        editionTitle={editionTitle}
        title={title}
        description={description}
      />
    </React.Fragment>
  );
};

ProductDLCContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      slug: PropTypes.string,
      id: PropTypes.string,
      starid: PropTypes.string,
    }),
  }).isRequired,
};

export default injectIntl(ProductDLCContainer);
